<template>
  <div class="bt">
    <section v-for="r in rounds" :key="r.key" class="bt-col" :class="'bt-' + r.key">
      <h2 class="bt-title">{{ r.label }}</h2>
      <div class="bt-body">
        <slot v-if="r.key === 'champ'" name="champ-above" />
        <div v-for="m in r.matches" :key="'w' + m.i" class="bt-wrap">
          <div class="bt-card" :class="{ live: m.i === currentMatchIndex, on: m.i === featured, tbd: !m.l.units || !m.r.units, champ: m.i === 7 }"
             role="button" tabindex="0" :aria-label="cardAction(m).label" @click="cardClick(m)" @keydown.enter.self="cardClick(m)">
          <span class="bt-map" :style="{ backgroundImage: m.mapNum != null ? `url(${mapImg(m.mapNum)})` : 'none' }"></span>
          <span class="bt-shade"></span>
          <span class="bt-head" v-tip="() => matchTip(m)">
            <span class="bt-kicker">{{ m.i === currentMatchIndex ? 'Live' : m.short }}</span>
            <span class="bt-mapname">{{ m.mapName }}</span>
            <button v-if="m.l.units && m.r.units" class="bt-fight" @click.stop="$emit('fight', m)" aria-label="Battle lab" v-tip="'Run this match in the Battle lab'">⚔</button>
          </span>
          <span class="bt-field">
            <span v-for="(side, s) in [m.l, m.r]" :key="s" class="bt-side" :class="[s ? 'right' : 'left', { lost: m.winner && side.key && m.winner !== side.key, won: side.key && m.winner === side.key }]">
              <span v-if="side.key" class="bt-team" :style="{ color: side.bright }">
                <span v-if="side.key === 'champion'" class="bt-crown">👑</span><button class="bt-teamname" @click.stop="$emit('team', side.key)" v-tip="() => sideTip(side)">{{ side.label }}</button><b v-if="m.winner === side.key"> ✓</b>
                <i v-if="side.units && grades" class="lx-grade bt-grade" :style="{ background: teamG(side).color }" v-tip="() => sideTip(side)">{{ teamG(side).grade }}</i>
              </span>
              <span v-if="side.units" class="bt-formation">
                <span v-for="(u, k) in side.units" :key="k" class="bt-unit" :class="['slot' + k, { boss: u.RaidBoss, tracked: trackColor(u) }]"
                      :style="trackColor(u) ? { '--track': trackColor(u) } : null" v-tip="() => unitTipFor(u, side, s ? m.l : m.r)">
                  <UnitSprite :unit="u" :team-color="side.pal" :fallback-src="portrait(u, side.label)" :flip="!s" :size="m.i === 7 ? 72 : m.round === 'qf' ? 52 : 56" />
                  <span v-if="u.RaidBoss" class="bt-boss">★</span>
                </span>
              </span>
              <span v-if="side.units" class="bt-names">
                <span v-for="(u, k) in side.units" :key="k" class="bt-name" :class="{ tracked: trackColor(u) }" :style="trackColor(u) ? { '--track': trackColor(u) } : null"
                      v-tip="() => unitTipFor(u, side, s ? m.l : m.r)"><span v-if="u.RaidBoss" class="bt-name-boss">★</span><span class="bt-name-txt">{{ u.Name }}</span><i v-if="grades" class="lx-grade" :style="{ background: unitG(u, side, k).color }">{{ unitG(u, side, k).grade }}</i></span>
              </span>
              <span v-else class="bt-tbd" v-tip="() => pendingTip(side)">{{ side.from }}<template v-if="side.cands.length && m.i !== 7"><br><span class="bt-cands">
                <template v-for="(c, ci) in side.cands" :key="c.key"><template v-if="ci">{{ ci === side.cands.length - 1 ? ' or ' : ', ' }}</template><b :style="{ color: c.bright }">{{ c.label }}</b></template></span></template></span>
            </span>
            <span class="bt-vs" v-tip="() => matchTip(m)">VS</span>
          </span>
          <span v-if="hasOdds(m) && oddsOf(m) !== 'failed'" class="bt-odds" v-tip="() => oddsTip(m)"><template v-if="oddsOf(m) && oddsOf(m).word"><template v-if="oddsOf(m).word === 'Toss-up'">Toss-up</template><template v-else><b :style="{ color: oddsOf(m).favBright }">{{ oddsOf(m).fav }}</b> {{ oddsOf(m).word.toLowerCase() }}</template></template><i v-else>calculating…</i></span>
          <!-- the champion card's own strip: the streak medallion and who's coming -->
          <span v-if="m.i === 7" class="bt-champinfo">
            <ChampStreakBadge v-if="champStreak" :streak="champStreak" size="lg" />
            <span class="bt-ci-text">
              <span class="bt-ci-line">{{ champLine(m) }}</span>
            </span>
          </span>
          </div>
          <div v-if="m.i === 7" class="bt-below"><slot name="champ-below" /></div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
// The homepage's bracket (2026-10 refresh): all 8 matches as a four-column tree of brass-framed cards on each match's
// map art, the two teams in formation facing each other. Winners get a ✓ and the losers fade; the live match pulses
// and says "Live"; the champion has its crown and streak badge; raid bosses glow gold with a ★; highlighted players'
// units get a ring in their color. A side not decided yet shows "Winner of …" and the teams that could still get there.
// Click a card to put it in the spotlight (`feature`); a card with only one team known opens that team's card instead
// (`team`, where its possible opponents are), as does a team's name. ⚔ opens the Battle lab (`fight`). The champion
// match is a bigger card with the streak medallion, the champion's roster and who's coming. Everything hovers with
// the site's tooltip (lib/tip.js): units, teams, the teams still in the running, and each match's map and how it leans. Extras can ride in the
// champion column through the `champ-above` / `champ-below` slots (the skill-drop chip sits below).
import UnitSprite from '../UnitSprite.vue';
import ChampStreakBadge from '../ChampStreakBadge.vue';
import { matchColors } from '../../lib/spriteForClass.js';
import { teamBright } from '../../lib/teamColors.js';
import { UNITS_BASE, MAPS_BASE } from '../../lib/assetUrls.js';
import { teamGrade } from '../../lib/unitGrade.js';
import { rosterStats } from '../../lib/preload.js';
import { loadData } from '../../lib/dataStore.js';
import { unitTip, teamTip } from '../../lib/unitTip.js';
import { cachedMatchOdds, oddsKey } from '../spotlight/matchOdds.js';
import { oddsScaleTip } from '../../lib/oddsWords.js';

const ORDER = ['red', 'blue', 'green', 'yellow', 'white', 'black', 'purple', 'brown'];
const TITLES = ['Quarterfinal 1', 'Quarterfinal 2', 'Quarterfinal 3', 'Quarterfinal 4', 'Semifinal 1', 'Semifinal 2', 'Final', 'Champion match'];
const SHORT = ['QF 1', 'QF 2', 'QF 3', 'QF 4', 'SF 1', 'SF 2', 'Final', 'Champ'];
const ROUND = ['qf', 'qf', 'qf', 'qf', 'sf', 'sf', 'final', 'champ'];
// where an undecided side comes from: [left, right] feeder match per match
const FEED = { 4: [0, 1], 5: [2, 3], 6: [4, 5], 7: [6, null] };
const MAP_RE = /^\s*(\d+)\)\s*(.*)$/;

export default {
  name: 'BracketTree',
  components: { UnitSprite, ChampStreakBadge },
  props: {
    teams: { type: Array, required: true },             // [{ name, label, roster, palettes }]
    winners: { type: Array, default: () => [] },
    maps: { type: Array, default: () => [] },
    currentMatchIndex: { type: Number, default: -1 },
    featured: { type: Number, default: null },           // the match in the spotlight, outlined here
    champStreak: { type: Number, default: 0 },
    highlightColors: { type: [Map, Object], default: () => new Map() }, // tracked player name (lowercase) → color
    gameData: { type: Object, default: null },           // for the team grades (none → no grade badges)
  },
  emits: ['feature', 'fight', 'team'],
  data() { return { mapStats: null, odds: {} }; },
  mounted() { loadData('mapStats.json').then((d) => { this.mapStats = d; }).catch(() => {}); },
  computed: {
    byName() { const m = {}; for (const t of this.teams) m[t.name] = t; return m; },
    grades() { return !!this.gameData; },
    // the matches still to be fought with both teams known, the live one first: each gets worded odds, run one at a time
    oddsTodo() {
      const ms = this.rounds.flatMap((r) => r.matches).filter((m) => this.hasOdds(m));
      return ms.sort((a, b) => (b.i === this.currentMatchIndex) - (a.i === this.currentMatchIndex));
    },
    rounds() {
      const W = this.winners || [];
      const ms = [];
      for (let i = 0; i < 8; i++) {
        // a side is known once the match feeding it is decided
        const known = i < 4 ? [ORDER[2 * i], ORDER[2 * i + 1]] : i === 7 ? [W[6] || null, 'champion'] : [W[FEED[i][0]] || null, W[FEED[i][1]] || null];
        const sides = known.map((key, k) => {
          const t = key && this.byName[key];
          if (t && (t.roster || []).length) return { key, label: t.label, units: t.roster, palettes: t.palettes, team: t, bright: teamBright(t.label) };
          const feed = FEED[i] && FEED[i][k];
          const cands = feed != null ? this.candidates(feed).map((c) => this.byName[c]).filter(Boolean).map((tm) => ({ key: tm.name, label: tm.label, team: tm, units: tm.roster, bright: teamBright(tm.label) })) : [];
          return { key: null, label: '', units: null, bright: '#8b93a3', from: feed != null ? 'Winner of ' + SHORT[feed] : 'To be decided', fromTitle: feed != null ? TITLES[feed] : '', cands };
        });
        const [l, r] = sides;
        if (l.team && r.team) [l.pal, r.pal] = matchColors(l.team, r.team);
        else { if (l.team) [l.pal] = matchColors(l.team, null); if (r.team) [r.pal] = matchColors(r.team, null); }
        const mm = MAP_RE.exec(this.maps[i] || '');
        ms.push({ i, round: ROUND[i], title: TITLES[i], short: SHORT[i], l, r, winner: W[i] || null, map: this.maps[i] || '', mapNum: mm ? +mm[1] : null, mapName: mm ? mm[2] : (this.maps[i] || '') });
      }
      return [
        { key: 'qf', label: 'Quarterfinals', matches: ms.slice(0, 4) },
        { key: 'sf', label: 'Semifinals', matches: ms.slice(4, 6) },
        { key: 'final', label: 'Final', matches: ms.slice(6, 7) },
        { key: 'champ', label: 'Champion match', matches: ms.slice(7) },
      ];
    },
  },
  watch: {
    winners() { this._tg = null; },
    oddsTodo: { immediate: true, handler() { this.pumpOdds(); } },
  },
  methods: {
    hasOdds(m) { return this.grades && !m.winner && !!m.l.units && !!m.r.units; },
    oddsOf(m) { return this.odds[oddsKey(m.l, m.r, m.map)]; },
    oddsTip(m) { const o = this.oddsOf(m); return o && o.word ? oddsScaleTip(o.word, o.fav) : 'Simulating this match…'; },
    // the same run the spotlight and team card use (cachedMatchOdds), so a match is simulated once
    async pumpOdds() {
      if (this._pumping) return;
      this._pumping = true;
      try {
        for (;;) {
          const m = this.oddsTodo.find((x) => !this.odds[oddsKey(x.l, x.r, x.map)]);
          if (!m) break;
          const key = oddsKey(m.l, m.r, m.map);
          this.odds = { ...this.odds, [key]: 'running' };
          try { const o = await cachedMatchOdds(m.l, m.r, m.map, this.gameData); this.odds = { ...this.odds, [key]: o }; }
          catch (e) { this.odds = { ...this.odds, [key]: 'failed' }; }
        }
      } finally { this._pumping = false; }
    },
    // the teams that can still come out of match f (its known sides, or recursively their feeders')
    candidates(f) {
      const W = this.winners || [];
      if (W[f]) return [W[f]];
      if (f < 4) return [ORDER[2 * f], ORDER[2 * f + 1]];
      return (FEED[f] || []).flatMap((g, k) => (g == null ? (f === 7 && k === 1 ? ['champion'] : []) : this.candidates(g)));
    },
    // a card with just one team known opens that team's card (its possible opponents); otherwise the spotlight
    cardAction(m) {
      const only = !m.winner && (!!m.l.units !== !!m.r.units) ? (m.l.units ? m.l : m.r) : null;
      return only ? { team: only.key, label: 'Open the ' + only.label + ' team card — its opponent isn’t known yet' } : { label: 'Show ' + m.title + ' in the spotlight' };
    },
    cardClick(m) { const a = this.cardAction(m); if (a.team) this.$emit('team', a.team); else this.$emit('feature', m.i); },
    unitG(u, side, k) { const tg = this.teamG(side); return (tg.units && tg.units[k]) || {}; },
    unitTipFor(u, side, foe) {
      const k = side.units.indexOf(u);
      return unitTip(u, { team: side.label, color: side.bright, grade: this.grades ? this.unitG(u, side, k) : null, gd: this.gameData,
        foes: foe && foe.units, foeLabel: foe && foe.label, mates: side.units });
    },
    sideTip(side) {
      const lines = side.key === 'champion' && this.champStreak ? ['Reigning champion · ' + this.champStreak + ' defense' + (this.champStreak === 1 ? '' : 's') + ' in a row'] : [];
      return teamTip(side.label, side.units, { color: side.bright, tg: this.grades ? this.teamG(side) : null, lines, foot: 'Click for the full team card' });
    },
    pendingTip(side) {
      if (!side.cands.length) return null;
      return {
        kicker: side.fromTitle ? 'Winner of ' + side.fromTitle : 'Still to be decided',
        title: side.cands.map((c) => c.label).join(' or '),
        rows: side.cands.map((c) => { const tg = this.grades ? this.teamG(c) : null; return [c.label, tg ? '~' + tg.score + '% expected' : 'in the running', null, tg && { text: tg.grade, color: tg.color }]; }),
        foot: 'Click the card for the known team’s possible opponents',
      };
    },
    matchTip(m) {
      const st = this.mapStats && this.mapStats.maps && this.mapStats.maps[m.mapName];
      const names = [m.l, m.r].map((s) => (s.units ? s.label : s.cands.length ? s.cands.map((c) => c.label).join('/') : s.from));
      const status = m.winner ? 'Won by ' + (this.byName[m.winner] || {}).label : m.i === this.currentMatchIndex ? 'Fighting now' : m.l.units && m.r.units ? 'Up next' : 'Waiting on ' + [m.l, m.r].filter((s) => !s.units).map((s) => s.from.replace('Winner of ', '')).join(' and ');
      const rows = [['Map', m.mapName ? m.mapName + (m.mapNum != null ? ' (#' + m.mapNum + ')' : '') : '—'], ['Status', status]];
      const chips = [];
      if (st) {
        if (st.side && st.side.first != null && Math.abs(st.side.first - 50) >= 1) rows.push(['Start side', 'the left team wins ' + st.side.first + '% here']);
        for (const c of (st.classes && st.classes.favors || []).filter((c) => c.dev >= 1).slice(0, 3)) chips.push({ text: '▲ ' + c.name + ' +' + c.dev, color: '#8fe0a0' });
        for (const c of (st.classes && st.classes.hurts || []).filter((c) => c.dev <= -1).slice(0, 3)) chips.push({ text: '▼ ' + c.name + ' ' + c.dev, color: '#f08a7a' });
      }
      return { kicker: m.title, title: names.join(' vs '), rows, chips, lines: chips.length ? ['Class win rates on this map vs their usual, in points'] : [], foot: this.cardAction(m).label };
    },
    champLine(m) {
      const ch = m.l;
      if (m.winner) return m.winner === 'champion' ? 'The champion held the belt.' : ((this.byName[m.winner] || {}).label || 'The challenger') + ' took the belt!';
      if (ch.units) return ch.label + ' challenges for the belt' + (this.grades ? ' (team ' + this.teamG(ch).grade + ' vs ' + this.teamG(m.r).grade + ')' : '') + '.';
      return ch.cands.length ? 'The challenger will be ' + ch.cands.map((c) => c.label).join(' or ') + '.' : 'Waiting on the Final.';
    },
    portrait(u, label) {
      if (u.Gender === 'Monster') return UNITS_BASE + u.Class + '.gif';
      const g = (u.Gender || 'Male')[0];
      return label === 'Champion' ? UNITS_BASE + u.Class + g + '.gif' : UNITS_BASE + u.Class + g + '_' + label + '.gif';
    },
    mapImg(n) { return MAPS_BASE + n + '_1.gif'; },
    teamG(side) { this._tg = this._tg || new Map(); if (!this._tg.has(side.units)) this._tg.set(side.units, teamGrade(side.units, rosterStats, this.gameData)); return this._tg.get(side.units); },
    trackColor(u) {
      const h = this.highlightColors, n = (u.Name || '').toLowerCase();
      if (!h || !n) return null;
      return (h instanceof Map ? h.get(n) : h[n]) || null;
    },
  },
};
</script>

<style scoped>
.bt { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 0 22px; margin: 6px 0 16px; text-align: left; }
@media (max-width: 1000px) { .bt { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 14px; } }
@media (max-width: 600px) { .bt { grid-template-columns: minmax(0, 1fr); } }
.bt-col { display: flex; flex-direction: column; min-width: 0; }
.bt-title { align-self: center; margin: 0 0 8px; padding: 2px 14px; border-radius: 4px; background: rgba(8,10,18,.82); font-family: var(--display); font-weight: 700; font-size: 16px; color: var(--brass-hi); }
.bt-body { flex: 1; display: flex; flex-direction: column; justify-content: space-around; gap: 12px; }
.bt-card { position: relative; display: block; min-height: 240px; cursor: pointer; overflow: hidden; color: var(--ink); outline: none;
  border: 1px solid var(--brass-edge); border-radius: 4px; background: var(--panel-bottom); box-shadow: 0 4px 12px rgba(0,0,0,.45); }
.bt-card::before { content: ''; position: absolute; inset: 0; z-index: 3; pointer-events: none;
  background:
    linear-gradient(var(--frame-hi), var(--frame-hi)) left 0 top 0 / 10px 2px no-repeat, linear-gradient(var(--frame-hi), var(--frame-hi)) left 0 top 0 / 2px 10px no-repeat,
    linear-gradient(var(--frame-hi), var(--frame-hi)) right 0 top 0 / 10px 2px no-repeat, linear-gradient(var(--frame-hi), var(--frame-hi)) right 0 top 0 / 2px 10px no-repeat,
    linear-gradient(var(--frame-hi), var(--frame-hi)) left 0 bottom 0 / 10px 2px no-repeat, linear-gradient(var(--frame-hi), var(--frame-hi)) left 0 bottom 0 / 2px 10px no-repeat,
    linear-gradient(var(--frame-hi), var(--frame-hi)) right 0 bottom 0 / 10px 2px no-repeat, linear-gradient(var(--frame-hi), var(--frame-hi)) right 0 bottom 0 / 2px 10px no-repeat; }
.bt-card:hover, .bt-card:focus-visible { border-color: var(--brass); }
.bt-card.on { border-color: var(--brass-hi); box-shadow: 0 0 0 2px var(--brass), 0 6px 20px rgba(217,180,94,.3); }
/* the live match pulses */
.bt-card.live { animation: bt-pulse 1.6s ease-in-out infinite; }
@keyframes bt-pulse { 0%, 100% { box-shadow: 0 0 4px 1px rgba(243,214,142,.45); } 50% { box-shadow: 0 0 16px 4px rgba(243,214,142,.9); } }
@media (prefers-reduced-motion: reduce) { .bt-card.live { animation: none; box-shadow: 0 0 10px 3px rgba(243,214,142,.7); } }
.bt-card.live .bt-kicker { color: #fff; background: #b8322a; padding: 0 6px; border-radius: 2px; }
.bt-map { position: absolute; inset: 0; background-size: cover; background-position: center; image-rendering: pixelated; filter: saturate(.75); }
.bt-card.tbd .bt-map { filter: saturate(.3) brightness(.6); }
.bt-shade { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(8,10,18,.82), rgba(8,10,18,.35) 35%, rgba(8,10,18,.6)); }
.bt-head { position: absolute; top: 5px; left: 8px; right: 8px; z-index: 2; display: flex; gap: 6px; align-items: baseline; }
.bt-kicker { font-family: var(--cond); font-weight: 700; font-size: 12px; letter-spacing: 1.4px; text-transform: uppercase; color: var(--brass); }
.bt-mapname { flex: 1; min-width: 0; text-align: right; font-size: 11px; color: #d8d0bb; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.bt-fight { flex: none; padding: 0 5px; font-size: 12px; line-height: 16px; background: rgba(8,10,18,.6); color: var(--brass-hi); border: 1px solid var(--brass-edge); border-radius: 3px; cursor: pointer; }
.bt-fight:hover { background: rgba(217,180,94,.25); }
.bt-field { position: relative; z-index: 2; display: flex; justify-content: space-between; margin: 24px 6px 6px; }
.bt-side { position: relative; width: 46%; display: flex; flex-direction: column; transition: opacity .3s; }
.bt-side.right { align-items: flex-end; }
.bt-side.lost { opacity: .4; filter: grayscale(.7); }
.bt-team { display: flex; align-items: center; gap: 4px; font-family: var(--cond); font-weight: 700; font-size: 14px; text-transform: uppercase; letter-spacing: .6px; text-shadow: 0 1px 0 #000, 0 0 6px rgba(0,0,0,.8); white-space: nowrap; }
.bt-team b { color: var(--brass-hi); }
.bt-teamname { padding: 0; border: 0; background: none; font: inherit; color: inherit; text-transform: inherit; letter-spacing: inherit; text-shadow: inherit; cursor: pointer; }
.bt-teamname:hover, .bt-teamname:focus-visible { text-decoration: underline; text-underline-offset: 2px; outline: none; }
.bt-grade { font-size: 11px; padding: 0 4px; letter-spacing: 0; }
.bt-crown { text-shadow: none; }
.bt-formation { position: relative; flex: none; width: 100%; height: 108px; }
.bt-unit { position: absolute; filter: drop-shadow(0 3px 2px rgba(0,0,0,.6)); }
.bt-unit.boss { filter: drop-shadow(0 0 5px gold) drop-shadow(0 0 2px gold); }
.bt-unit.tracked { filter: drop-shadow(0 0 4px var(--track)) drop-shadow(0 0 2px var(--track)); }
.bt-boss { position: absolute; top: -4px; right: -4px; color: gold; font-size: 12px; text-shadow: 0 1px 0 #000; }
.bt-side.left .slot0 { left: 48%; top: 0; } .bt-side.left .slot1 { left: 6%; top: 0; } .bt-side.left .slot2 { left: 58%; top: 40%; } .bt-side.left .slot3 { left: 16%; top: 40%; }
.bt-side.right .slot0 { right: 48%; top: 0; } .bt-side.right .slot1 { right: 6%; top: 0; } .bt-side.right .slot2 { right: 58%; top: 40%; } .bt-side.right .slot3 { right: 16%; top: 40%; }
/* the worded odds for a match still to come, kept vague on purpose (numbers live in the Battle lab and the team card) */
.bt-odds { position: relative; z-index: 2; display: block; margin: -2px 8px 7px; text-align: center; font-family: var(--cond); font-weight: 600; font-size: 13.5px;
  letter-spacing: .3px; color: #f3ecd8; text-shadow: 0 1px 0 #000, 0 0 6px rgba(0,0,0,.9); }
.bt-odds b { font-weight: 700; }
.bt-odds:hover { text-decoration: underline dotted rgba(243,236,216,.5); text-underline-offset: 3px; }
.bt-odds i { font-weight: 400; color: var(--mute); }
.bt-card.champ .bt-odds { font-size: 15px; }
/* the players' names under each side's formation, with each unit's grade */
.bt-names { display: flex; flex-direction: column; gap: 1px; width: 100%; margin-top: 3px; }
.bt-side.right .bt-names { align-items: flex-end; }
.bt-name { display: flex; align-items: center; gap: 3px; max-width: 100%; font-family: var(--cond); font-weight: 600; font-size: 12.5px; line-height: 15px;
  color: var(--ink-hi); text-shadow: 0 1px 0 #000, 0 0 4px rgba(0,0,0,.9); }
.bt-side.right .bt-name { flex-direction: row-reverse; }
.bt-name-txt { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bt-name.tracked .bt-name-txt { color: var(--track); font-weight: 700; }
.bt-name-boss { color: gold; font-size: 11px; }
.bt-name .lx-grade { flex: none; font-size: 9.5px; line-height: 13px; padding: 0 3px; text-shadow: none; }
.bt-tbd { margin-top: 18px; font-size: 12px; font-style: italic; color: var(--mute); }
.bt-vs { position: absolute; left: 50%; top: 72px; transform: translate(-50%, -50%); width: 26px; height: 26px; border-radius: 50%; display: grid; place-items: center;
  font-family: var(--display); font-size: 10px; color: #1a1408; background: linear-gradient(#f3d68e, #a27a35); box-shadow: 0 2px 5px rgba(0,0,0,.6); }
.bt-wrap { position: relative; }
@media (min-width: 1001px) { .bt-below { position: absolute; top: 100%; left: 0; right: 0; margin-top: 12px; } }
@media (max-width: 1000px) { .bt-below { margin-top: 12px; } }
.bt-cands { font-style: normal; font-family: var(--cond); font-weight: 700; font-size: 13px; letter-spacing: .4px; text-transform: uppercase; text-shadow: 0 1px 0 #000; }
.bt-cands b { font-weight: 700; }

/* the champion match: a bigger card, centered on the Final, with an info strip under the field */
.bt-champ .bt-body { justify-content: center; }
.bt-card.champ { min-height: 300px; border-color: var(--brass); box-shadow: 0 0 0 1px rgba(243,214,142,.25), 0 8px 22px rgba(0,0,0,.55); }
.bt-card.champ .bt-field { margin-top: 30px; }
.bt-card.champ .bt-formation { height: 168px; }
.bt-card.champ .bt-name { font-size: 14px; line-height: 17px; }
.bt-card.champ .bt-team { font-size: 16px; }
.bt-card.champ .bt-side.left .slot2, .bt-card.champ .bt-side.left .slot3, .bt-card.champ .bt-side.right .slot2, .bt-card.champ .bt-side.right .slot3 { top: 46%; }
.bt-card.champ .bt-vs { top: 106px; width: 34px; height: 34px; font-size: 12px; }
.bt-champinfo { position: relative; z-index: 2; display: flex; align-items: center; gap: 10px; margin: 2px 8px 10px; padding: 8px 10px;
  border: 1px solid var(--brass-edge); border-radius: 4px; background: rgba(8,10,18,.78); }
.bt-ci-text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.bt-ci-line { font-size: 12.5px; color: var(--ink); }
</style>
